import React, { memo, useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack, Tooltip } from '@lyearn/ui';

import { MultiSelectValues, TagValues } from '../../../types';
import ActionButton from '../ActionButton';
import { TagsValueSelectorProps } from '../types';
import { getMultiSelectPlaceholder } from '../utils';
import MultiSelectComp from './MultiSelectComp';

function MultiSelectContainer(props: TagsValueSelectorProps) {
  const { t } = useTranslation();
  const [open, { toggle }] = useToggle(false);
  const {
    className,
    tag,
    selectedTagValue,
    onDelete,
    error,
    children,
    readOnly,
    disabledMessage,
    onChange: onTagsValueChange,
  } = props;
  const multiSelectTagValue = selectedTagValue as MultiSelectValues;
  const selectedLabels = useMemo(
    () => multiSelectTagValue?.map((option) => option.label),
    [multiSelectTagValue],
  );
  const placeholder = useMemo(() => getMultiSelectPlaceholder(selectedLabels), [selectedLabels]);

  const onChange = useCallback(
    (val: TagValues) => {
      onTagsValueChange(val);
    },
    [onTagsValueChange],
  );

  const isDisabled = Boolean(disabledMessage);

  const handleClick = () => {
    if (!readOnly && !isDisabled) {
      toggle();
    }
  };

  const renderChildren = () => {
    return children ? (
      children
    ) : (
      <ActionButton
        className={className}
        count={placeholder?.count}
        defaultPlaceholder={t('Tags:Form.SelectValue')}
        disabledMessage={disabledMessage}
        error={error}
        isInFocus={open}
        readOnly={readOnly}
        selectedTagValue={placeholder?.text}
        tag={tag}
        tooltipText={selectedLabels?.join(', ')}
        onDelete={onDelete}
      />
    );
  };

  const renderContent = () => {
    if (isDisabled) {
      return (
        <Tooltip title={disabledMessage}>
          <div>{renderChildren()}</div>
        </Tooltip>
      );
    }
    return renderChildren();
  };

  if (!open) {
    return (
      <Stack className={cn('max-w-full', className)} onClick={handleClick}>
        {renderContent()}
      </Stack>
    );
  }

  return (
    <MultiSelectComp {...props} toggle={toggle} onChange={onChange}>
      {renderContent()}
    </MultiSelectComp>
  );
}

export default memo(MultiSelectContainer);
